import { FC, memo, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Endpoints, TFile } from "@brg/api";
import { AppTester } from 'supporting';
import { useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LinkTo } from 'shared/constants/linkTo';
import getMetaElements from './getMetaElements';
import { useDomainsPlatform } from 'shared/hooks/useDomainsPlatform';
import { useAddCanonicalLink } from 'shared/hooks/useTeamCanonicalTag';
import useDocumentTitle from 'shared/components/AlteredHelmet/useDocumentTitle';

interface MetaTagsProps {
  title?: string;
  description?: string[];
  images?: TFile[];
  canonicalInfo?: {
    isCanonical: boolean;
    contextInfos: Array<{
      domain: string;
      canonicalType: number;
      protocol?: string;
      path?: string;
    }>;
  };
  alternateDomainInfo?: Array<{
    domain: string;
    canonicalType?: number;
    protocol?: string;
  }>;
  canonicalUrl?: string;
  isAgeRestricted?: boolean;
}

const TARGET_SIZE = 1152;

const pathWithoutLeadingContext = () => {
  const { pathname, search } = window.location;
  let from = -1;
  for (const m of ['/project/', '/tag/', '/teams/']) {
    const i = pathname.indexOf(m);
    if (i >= 0 && (from < 0 || i < from)) from = i;
  }
  return (from >= 0 ? pathname.slice(from) : pathname) + search;
};

const MetaTags: FC<MetaTagsProps> = ({
  title,
  description,
  images,
  canonicalInfo,
  alternateDomainInfo,
  canonicalUrl,
  isAgeRestricted,
}) => {
  const { i18n } = useTranslation();
  const isProjectPage = !!useRouteMatch('/project');
  const isTagPage = !!useRouteMatch('/tag');
  const isProfile = !!useRouteMatch(LinkTo.designer);
  const domainsMap = useDomainsPlatform(isTagPage);

  const img = useMemo(() => {
    if (!!images) {
      const sorted = [...images]?.sort((a, b) => a.width - b.width)

      const resized = sorted?.find(({ width }) => width >= TARGET_SIZE)
      const original = [...images]?.find(({ size }) => size === 'original');

      return resized || original || images[0];

    }
    return null;
  }, [TARGET_SIZE, images]);

  const computedTitle = useDocumentTitle(title);

  const clearMetaTags = () => {
    const tags = document.querySelectorAll(`[data-custom-tag="true"]`)
    tags.forEach((el) => {
      el.remove();
    })
  }

  useEffect(() => {
    clearMetaTags()
    const imageUrl = img && !isAgeRestricted
      ? `${Endpoints.files}/${img?.url}`
      : isAgeRestricted
        ? '/brg/meta/blurFor18.jpg'
        : null;

    const elements = getMetaElements({title: computedTitle, imageUrl, description, url: window.location.href})

    elements.forEach((el: any) => {
      const meta = document.createElement('meta')
      Object.entries(el).forEach(([key, value]) => {
        meta.setAttribute(key, value as string);
      });
      meta.setAttribute('data-custom-tag', 'true');
      document.head.prepend(meta);
    })
    return clearMetaTags
  }, [title, description, img])
  
  const { canonicalUrl: computedCanonicalUrl, alternateUrls } = useMemo(() => {
    if (!canonicalInfo || !canonicalInfo.contextInfos?.length) {
      return { canonicalUrl: null, alternateUrls: [] };
    }

    const canonicalDomain = canonicalInfo.contextInfos[0] as any;
    
    const protocol = canonicalDomain?.protocol 
      ? `${canonicalDomain.protocol}:` 
      : window.location.protocol;
    
    const isTeamHomePage = window.location.pathname.match(/^\/teams\/[^\/]+$/);
    const path = (() => {
      const segment =
        isTeamHomePage && canonicalDomain?.path
          ? canonicalDomain.path
          : pathWithoutLeadingContext();
      return segment.startsWith('/') ? segment : `/${segment}`;
    })();

    const buildCanonicalUrl = (domain: string | undefined, protocolWithColon: string) =>
      domain ? `${protocolWithColon}//${domain.split('/')[0].split('?')[0]}${path}` : null;

    const computedUrl = buildCanonicalUrl(canonicalDomain?.domain, protocol);

    if (alternateDomainInfo && alternateDomainInfo.length > 0) {
      const alternateUrls = alternateDomainInfo
        .map((ctx: any) => {
          const altProtocol = ctx.protocol ? `${ctx.protocol}:` : protocol;
          return buildCanonicalUrl(ctx.domain, altProtocol);
        })
        .filter(Boolean) as string[];
      return { canonicalUrl: computedUrl, alternateUrls };
    }

      if (isTagPage && canonicalInfo.contextInfos.length === 2) {
        const deziiignDomain = canonicalInfo.contextInfos.find(ctx => {
          const platform = domainsMap.get(ctx.domain);
          return platform === 'deziiign';
        }) as any;
        const galllleryDomain = canonicalInfo.contextInfos.find(ctx => {
          const platform = domainsMap.get(ctx.domain);
          return platform === 'gallery';
        }) as any;
        const dezProtocol = deziiignDomain?.protocol ? `${deziiignDomain.protocol}:` : protocol;
        const galProtocol = galllleryDomain?.protocol ? `${galllleryDomain.protocol}:` : protocol;
      const alternateUrl = buildCanonicalUrl(galllleryDomain?.domain, galProtocol);
        return {
          canonicalUrl: buildCanonicalUrl(deziiignDomain?.domain, dezProtocol),
          alternateUrls: alternateUrl ? [alternateUrl] : []
        };
      }

        const alternateUrls = canonicalInfo.contextInfos
          .slice(1)
          .map((ctx: any) => {
            const altProtocol = ctx.protocol ? `${ctx.protocol}:` : protocol;
            return buildCanonicalUrl(ctx.domain, altProtocol);
      })
      .filter(Boolean) as string[];

        return { canonicalUrl: computedUrl, alternateUrls };
  }, [canonicalInfo, alternateDomainInfo, isTagPage, domainsMap]);

  useEffect(() => {
    if (computedTitle) {
      document.title = computedTitle;
    }
  }, [computedTitle]);
  
  const canonicalLinkUrl = canonicalUrl ?? computedCanonicalUrl ?? undefined;
  
  useAddCanonicalLink(canonicalLinkUrl);

  return (
    <Helmet>
      <title>
        {computedTitle}
      </title>
      {canonicalLinkUrl && <link rel="canonical" href={canonicalLinkUrl} />}
      {canonicalUrl === undefined && alternateUrls.map((url, index) => (
        <link key={index} rel="alternate" href={url} />
      ))}
    </Helmet>
  );
}

export default memo(MetaTags);
import { FC, memo, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Endpoints, TFile } from "@brg/api";
import { AppTester } from 'supporting';
import { useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LinkTo } from 'shared/constants/linkTo';
import getMetaElements from './getMetaElements';
import { useDomainsPlatform } from 'shared/hooks/useDomainsPlatform';
import { useAddCanonicalLink } from 'shared/hooks/useTeamCanonicalTag';
import useDocumentTitle from 'shared/components/AlteredHelmet/useDocumentTitle';

interface MetaTagsProps {
  title?: string;
  description?: string[];
  images?: TFile[];
  canonicalInfo?: {
    isCanonical: boolean;
    contextInfos: Array<{
      domain: string;
      canonicalType: number;
      protocol?: string;
      path?: string;
    }>;
  };
  alternateDomainInfo?: Array<{
    domain: string;
    canonicalType?: number;
    protocol?: string;
  }>;
  canonicalUrl?: string;
  isAgeRestricted?: boolean;
}

const TARGET_SIZE = 1152;

const pathWithoutLeadingContext = () => {
  const { pathname, search } = window.location;
  let from = -1;
  for (const m of ['/project/', '/tag/', '/teams/']) {
    const i = pathname.indexOf(m);
    if (i >= 0 && (from < 0 || i < from)) from = i;
  }
  return (from >= 0 ? pathname.slice(from) : pathname) + search;
};

const MetaTags: FC<MetaTagsProps> = ({
  title,
  description,
  images,
  canonicalInfo,
  alternateDomainInfo,
  canonicalUrl,
  isAgeRestricted,
}) => {
  const { i18n } = useTranslation();
  const isProjectPage = !!useRouteMatch('/project');
  const isTagPage = !!useRouteMatch('/tag');
  const isProfile = !!useRouteMatch(LinkTo.designer);
  const domainsMap = useDomainsPlatform(isTagPage);

  const img = useMemo(() => {
    if (!!images) {
      const sorted = [...images]?.sort((a, b) => a.width - b.width)

      const resized = sorted?.find(({ width }) => width >= TARGET_SIZE)
      const original = [...images]?.find(({ size }) => size === 'original');

      return resized || original || images[0];

    }
    return null;
  }, [TARGET_SIZE, images]);

  const computedTitle = useDocumentTitle(title);

  const clearMetaTags = () => {
    const tags = document.querySelectorAll(`[data-custom-tag="true"]`)
    tags.forEach((el) => {
      el.remove();
    })
  }

  useEffect(() => {
    clearMetaTags()
    const imageUrl = img && !isAgeRestricted
      ? `${Endpoints.files}/${img?.url}`
      : isAgeRestricted
        ? '/brg/meta/blurFor18.jpg'
        : null;

    const elements = getMetaElements({title: computedTitle, imageUrl, description, url: window.location.href})

    elements.forEach((el: any) => {
      const meta = document.createElement('meta')
      Object.entries(el).forEach(([key, value]) => {
        meta.setAttribute(key, value as string);
      });
      meta.setAttribute('data-custom-tag', 'true');
      document.head.prepend(meta);
    })
    return clearMetaTags
  }, [title, description, img])
  
  const { canonicalUrl: computedCanonicalUrl, alternateUrls } = useMemo(() => {
    if (!canonicalInfo || !canonicalInfo.contextInfos?.length) {
      return { canonicalUrl: null, alternateUrls: [] };
    }

    const canonicalDomain = canonicalInfo.contextInfos[0] as any;
    
    const protocol = canonicalDomain?.protocol 
      ? `${canonicalDomain.protocol}:` 
      : window.location.protocol;
    
    const isTeamHomePage = window.location.pathname.match(/^\/teams\/[^\/]+$/);
    const path = (() => {
      const segment =
        isTeamHomePage && canonicalDomain?.path
          ? canonicalDomain.path
          : pathWithoutLeadingContext();
      return segment.startsWith('/') ? segment : `/${segment}`;
    })();

    const buildCanonicalUrl = (domain: string | undefined, protocolWithColon: string) =>
      domain ? `${protocolWithColon}//${domain.split('/')[0].split('?')[0]}${path}` : null;

    const computedUrl = buildCanonicalUrl(canonicalDomain?.domain, protocol);

    if (alternateDomainInfo && alternateDomainInfo.length > 0) {
      const alternateUrls = alternateDomainInfo
        .map((ctx: any) => {
          const altProtocol = ctx.protocol ? `${ctx.protocol}:` : protocol;
          return buildCanonicalUrl(ctx.domain, altProtocol);
        })
        .filter(Boolean) as string[];
      return { canonicalUrl: computedUrl, alternateUrls };
    }

      if (isTagPage && canonicalInfo.contextInfos.length === 2) {
        const deziiignDomain = canonicalInfo.contextInfos.find(ctx => {
          const platform = domainsMap.get(ctx.domain);
          return platform === 'deziiign';
        }) as any;
        const galllleryDomain = canonicalInfo.contextInfos.find(ctx => {
          const platform = domainsMap.get(ctx.domain);
          return platform === 'gallery';
        }) as any;
        const dezProtocol = deziiignDomain?.protocol ? `${deziiignDomain.protocol}:` : protocol;
        const galProtocol = galllleryDomain?.protocol ? `${galllleryDomain.protocol}:` : protocol;
      const alternateUrl = buildCanonicalUrl(galllleryDomain?.domain, galProtocol);
        return {
          canonicalUrl: buildCanonicalUrl(deziiignDomain?.domain, dezProtocol),
          alternateUrls: alternateUrl ? [alternateUrl] : []
        };
      }

        const alternateUrls = canonicalInfo.contextInfos
          .slice(1)
          .map((ctx: any) => {
            const altProtocol = ctx.protocol ? `${ctx.protocol}:` : protocol;
            return buildCanonicalUrl(ctx.domain, altProtocol);
      })
      .filter(Boolean) as string[];

        return { canonicalUrl: computedUrl, alternateUrls };
  }, [canonicalInfo, alternateDomainInfo, isTagPage, domainsMap]);

  useEffect(() => {
    if (computedTitle) {
      document.title = computedTitle;
    }
  }, [computedTitle]);
  
  const canonicalLinkUrl = canonicalUrl ?? computedCanonicalUrl ?? undefined;
  
  useAddCanonicalLink(canonicalLinkUrl);

  return (
    <Helmet>
      <title>
        {computedTitle}
      </title>
      {canonicalLinkUrl && <link rel="canonical" href={canonicalLinkUrl} />}
      {canonicalUrl === undefined && alternateUrls.map((url, index) => (
        <link key={index} rel="alternate" href={url} />
      ))}
    </Helmet>
  );
}

export default memo(MetaTags);
